import { reactive } from 'vue'
import store from './store'
import moment from 'moment'
import {
	getMyMembershipForBubble,
	setMemberships,
	setUser,
} from './memberships'
import { setMessage } from './messages'
import { isGroupInRoute, waitIfKey } from '../helperMethods'
import { timedLogger } from '@/helpers/log'
import { compareDates, isDateISO } from '@/helpers/utils'
import { importTables } from '@/store/database'
import { MAX_RECENT_CHATS } from '@/helpers/constants'

const chatsById = reactive({})
const chatsByCategoryId = reactive({})
const statsByChatId = reactive({})
const categoriesById = reactive({})
const videoSessionsById = reactive({})

export const chatsData = {
	chatsById,
	chatsByCategoryId,
	statsByChatId,
	categoriesById,
	videoSessionsById,
}

// chat related functions
export const normalizeChat = (chat, skipDB) => {
	if (!chat) return null
	let {
		category,
		dmpartner,
		memberships,
		meeting,
		videosession,
		pinned_message,
		pinned_message_user,
		...rest
	} = chat
	setCategory(category)
	if (dmpartner) {
		setUser(dmpartner)
		rest.dmpartner_id = dmpartner.id
	}
	setMemberships(memberships, skipDB)
	mergeMembershipPropsFromChat(rest)
	setVideosession(meeting || videosession, skipDB)

	// Copy permissions to root level if they exist
	// TODO: remove this once the migration is complete
	if (rest.permissions) {
		Object.assign(rest, rest.permissions)
	}

	// Copy grants to root level with 'grant_' prefix
	// TODO: remove this once the migration is complete
	if (rest.grants) {
		Object.entries(rest.grants).forEach(([key, value]) => {
			rest[`grant_${key}`] = value
		})
	}

	rest.isDM = !!(rest.is_dm || rest.isdm)
	rest.isMultiDM = rest.isDM && rest.dm_memberships_count > 2
	rest.meeting_id = rest.meeting_id || rest.videosession_id
	rest.videosession_id = rest.meeting_id

	const stat = normalizeStat(getStat(rest.id))
	if (stat?.chat_id) {
		rest.unread = stat.unread
		rest.unread_mentions = stat.unread_mentions
		rest.stat_updated_at = stat.updated
		rest.mark = stat.mark
		rest.latest_message_id = stat.latest_message_id
		rest.latest_message_created_at = stat.latest_message_created_at
	}

	if (pinned_message) {
		setMessage(pinned_message)
		setUser(pinned_message_user)
	}
	return rest
}

export const setChat = (chat, skipDB) => {
	if (!chat) return null
	if (isOldChat(chat)) {
		chat = getChat(chat.id)
	} else {
		chat = normalizeChat(chat, skipDB)
		chatsById[chat.id] = chat
	}
	if (chat.category_id) {
		if (chatsByCategoryId[chat.category_id]) {
			chatsByCategoryId[chat.category_id][chat.id] = chat.id
		} else {
			chatsByCategoryId[chat.category_id] = { [chat.id]: chat.id }
		}
	}

	if (!skipDB) {
		store.dbPut('chats', chat)
	}
	EventBus.$emitDebounced('chat-update')

	return chat
}

export const setChats = (chats, skipDB) => {
	if (!chats) return null
	const normalized = chats
		.map((chat) => setChat(chat, true))
		.filter((chat) => chat)
	if (normalized.length && !skipDB) {
		store.dbBulkPut('chats', normalized)
	}
	EventBus.$emitDebounced('chat-update')
	return normalized
}

export const mergeMembershipPropsFromChat = (chat) => {
	const memb = getMyMembershipForBubble(chat.id)
	if (!memb?.id) return
	Object.assign(chat, {
		displayname: memb.alias || chat.title,
		mute: memb.mute || false,
		is_pinned: memb.is_pinned || false,
		marked_unread: memb.marked_unread || false,
		marked_unread_updated_at: memb.marked_unread_updated_at || null,
		ishidden: memb.ishidden || false,
		role: memb.role || null,
	})
}

export const removeChatFromCategory = (chat_id) => {
	const { category_id } = getChat(chat_id)
	if (chatsByCategoryId[category_id]) {
		delete chatsByCategoryId[category_id][chat_id]
	}
	delete chatsById[chat_id]
}

export const updateChat = (id, c = {}, skipDB) => {
	const chat = getChat(id) || {}
	return setChat({ ...chat, ...c }, skipDB)
}

export const deleteChat = (id) => {
	const chat = getChat(id)
	if (!chat) return null
	delete chatsById[id]

	// delete any reference to this chat in indexed_db
	store.dbDeleteByKey('chats', id)
	store.dbDeleteByColumn('messages', 'bubble_id', id)
	store.dbDeleteByColumn('memberships', 'bubble_id', id)
	if (chatsByCategoryId[chat.category_id]) {
		delete chatsByCategoryId[chat.category_id][id]
	}
	if (statsByChatId[id]) {
		delete [statsByChatId][id]
		store.dbDeleteByKey('stats', id)
	}

	EventBus.$emitDebounced('chat-update')
}

export const getChat = (chatId) => {
	if (!chatId) return null
	return chatsById[chatId] || {}
}

export const getCurrentChat = () => {
	if (!store.currentChatId) return {}
	return getChat(store.currentChatId)
}

export const getChats = () => {
	return Object.values(chatsById || {}).sort(sortChatsByLastMessage)
}

const compareString = (strA, strB) => {
	return strA?.localeCompare(strB) || 0
}

const compareInt = (intA, intB) => {
	return (intB || 0) - (intA || 0)
}

export const sortChatsById = (c1, c2) => {
	return compareInt(c1.id, c2.id)
}

export const sortChatsByMarkedUnread = (c1, c2) => {
	const c1Latest = c1.latest_message_created_at
	const c1Marked = c1.marked_unread_updated_at
	const c2Latest = c2.latest_message_created_at
	const c2Marked = c2.marked_unread_updated_at
	const date1 = compareDates(c1Latest, c1Marked) <= 0 ? c1Latest : c1Marked
	const date2 = compareDates(c2Latest, c2Marked) <= 0 ? c2Latest : c2Marked
	let diff = compareDates(date1, date2)
	if (diff === 0) diff = sortChatsByDisplayName(c1, c2)
	return diff
}

export const sortChatsByLatestMessage = (c1, c2) => {
	let diff = compareDates(
		c1.latest_message_created_at,
		c2.latest_message_created_at
	)
	if (diff === 0) diff = sortChatsByDisplayName(c1, c2)
	return diff
}

export const sortChatsByLastMessage = (c1, c2) => {
	let diff = compareInt(c1.latest_message_id, c2.latest_message_id)
	if (diff === 0) diff = sortChatsByDisplayName(c1, c2)
	return diff
}

export const sortChatsByDisplayName = (c1, c2) => {
	let diff = compareString(c1.displayname, c2.displayname)
	if (diff === 0) diff = sortChatsByTitle(c1, c2)
	if (diff === 0) diff = sortChatsById(c1, c2)
	return diff
}

export const sortChatsByTitle = (c1, c2) => {
	return compareString(c1.title, c2.title)
}

export const sortCategoriesByTitle = (c1, c2) => {
	return sortChatsByTitle(c1, c2)
}

export const sortCategoriesByDisplayName = (c1, c2) => {
	return compareString(c1.displayname, c2.displayname)
}

// return any chats that meet one of the following requirements:
//   a) last message was created less than 48 hours ago
//   b) chat has unread messages
//   c) chat has unread mentions
export const getRecentChats = () => {
	return Object.values(chatsById || {}).filter(isChatInRecents)
}

// return the actual list of chats for the "Recents" drawer section
// this follows the following logic:
//   a) display all chats with unread messages OR
//   b) display up to 10 recent chats with no unreads
export const getRecentChatsToDisplay = () => {
	const chats = getRecentChats().sort(sortChatsByActivity)
	const allUnreads = chats.filter((c) => c.unread > 0)
	return allUnreads.length > MAX_RECENT_CHATS
		? allUnreads
		: chats.slice(0, MAX_RECENT_CHATS)
}

export const isChatInRecents = (chat) => {
	if ((chat.mute && !chat.unread_mentions) || isChatHidden(chat.id))
		return false
	return (
		hoursOld(chat.latest_message_created_at) < 48 ||
		hoursOld(chat.marked_unread_updated_at) < 48 ||
		chat.unread > 0 ||
		chat.unread_mentions > 0 ||
		chat.marked_unread
	)
}

export const getPinnedChats = () => {
	return Object.values(chatsById || {})
		.filter(isChatInPinned)
		.sort(sortChatsByDisplayName)
}

export const isChatInPinned = (chat) => {
	return chat.is_pinned && !isChatHidden(chat.id)
}

// return any chats that are not DM's and are not hidden
export const getGroupChats = () => {
	return Object.values(chatsById || {}).filter((chat) => {
		return !chat.isdm && !isChatHidden(chat.id)
	})
}

// return any chats that are DM's
export const getDMChats = () => {
	return Object.values(chatsById || {}).filter((chat) => {
		return chat.isdm && !isChatHidden(chat.id)
	})
}

// return any chats that have a category, is not a DM, and is not hidden
export const getChatsWithNoCategory = () => {
	return Object.values(chatsById || {}).filter((chat) => {
		return chat.category_id === null && !chat.isdm && !isChatHidden(chat.id)
	})
}

export const getChatsWithCategory = () => {
	return Object.values(chatsById || {}).filter((chat) => {
		return chat.category_id && !chat.isdm && !isChatHidden(chat.id)
	})
}

export const getCategoriesWithChats = () => {
	return getCategories()
		.sort(sortCategoriesByDisplayName)
		.map((cat) => ({
			...cat,
			chats: getChatsForCategory(cat.id).sort(sortChatsByDisplayName),
		}))
}

// return any chats that have an unread count
export const getUnreadChats = () => {
	return Object.values(chatsById || {}).filter((chat) => {
		return chat.unread > 0
	})
}

export const getChatIdsForCategory = (categoryId) => {
	return Object.values(chatsByCategoryId[categoryId] || {})
}

export const getChatsForCategory = (categoryId) => {
	return getChatIdsForCategory(categoryId)
		.map(getChat)
		.filter((chat) => !isChatHidden(chat.id))
}

export const isChatHidden = (chatId) => {
	const membership = getMyMembershipForBubble(chatId)
	return !membership || (membership?.ishidden && !isGroupInRoute(chatId))
}

export const isChatMarkedAsUnread = (chatId) => {
	const membership = getMyMembershipForBubble(chatId)
	return !!membership?.marked_unread
}

export const getHiddenChats = () => {
	return Object.values(chatsById || {}).filter((chat) => chat.ishidden)
}

const isOldChat = (chat) => {
	if (!chat) return null
	const previous = getChat(chat.id)
	if (previous && chat.updated_at && previous.updated_at) {
		const isNewDateNewer =
			compareDates(chat.updated_at, previous.updated_at) > 0
		return isNewDateNewer
	}
	return false
}

const hoursOld = (datetime) => {
	if (!datetime) return Number.POSITIVE_INFINITY
	var utc = moment.utc(datetime).local()
	var hours = moment(new Date()).diff(utc, 'hours')
	return hours
}

export const importAllChats = async () => {
	let isListComplete = false
	const onListComplete = () => {
		// if bubble.list finishes first, then don't update from the cache
		isListComplete = true
	}
	EventBus.$on('bubbleListComplete', onListComplete)
	// leaving this logging in here at debug level so we can diagnose
	//  any future issues that might arise with slow db performance
	const log = timedLogger('chats|stats|cats')
	log('starting query')
	try {
		const [chats, stats, categories] = await importTables([
			'chats',
			'stats',
			'categories',
		])
		await waitIfKey('slowDB', 8000)
		log('query complete')
		if (isListComplete) {
			return log('bubble.list finished first')
		} else {
			setChats(chats, true)
			log('chats updated')
			setStats(stats, true)
			log('stats updated')
			setCategories(categories, true)
			log('categories updated, done')
		}
	} catch (error) {
		console.error('importAllChats', error)
	}
	log.print()
	EventBus.$off('bubbleListComplete', onListComplete)
}

export const diffChatsInCache = (chats) => {
	if (!chats || !chats.length) return
	// current list of chats passed in after a bubble.list call
	const chatIds = chats.map((c) => c.id)
	// get a list of ID's we currently have so we can compare and remove the ones that shouldn't be there
	// delete any chats that we don't need (this will delete the stats as well)
	getChats()
		.map((c) => c.id)
		.filter((id) => !chatIds.includes(id))
		.forEach(deleteChat)

	// do the same thing for categories
	const categoryIds = chats.map((c) => c.category_id).filter((c) => c)
	getCategories()
		.map((c) => c.id)
		.filter((id) => !categoryIds.includes(id))
		.forEach(deleteCategory)
}

// Category related functions

export const normalizeCategory = (category) => {
	if (!category) return null
	const { usercategory, user_category, ...rest } = category
	rest.user_category = user_category || usercategory
	rest.usercategory = rest.user_category // TODO: remove this once the migration is complete
	rest.displayname = rest.user_category?.alias || rest.title
	return rest
}

export const setCategory = (category, skipDB) => {
	if (!category) return null
	if (isOldCategory(category)) {
		// if chat is old, get previous (newer) version and use that
		category = getCategory(category.id)
	} else {
		category = normalizeCategory(category)
		categoriesById[category.id] = category
	}

	if (!skipDB) {
		store.dbPut('categories', category)
	}
	EventBus.$emitDebounced('chat-update')

	return category
}

export const setCategories = (categories, skipDB) => {
	if (!categories) return null
	const normalized = categories
		.map((category) => setCategory(category, true))
		.filter((c) => c)
	if (normalized.length && !skipDB) {
		store.dbBulkPut('categories', normalized)
		EventBus.$emitDebounced('chat-update')
	}
	return categories
}

export const getCategory = (categoryId) => {
	if (!categoryId) return null
	return categoriesById[categoryId] || null
}

export const updateCategory = (id, c = {}) => {
	const category = getCategory(id) || {}
	setCategory({ ...category, ...c })
}

export const deleteCategory = (id) => {
	const category = getCategory(id)
	if (!category) return null
	delete categoriesById[id]
	store.dbDeleteByKey('categories', id)

	EventBus.$emitDebounced('chat-update')
}

const isOldCategory = (category) => {
	if (!category) return null
	const previous = getCategory(category.id)
	if (previous && category.updated_at && previous.updated_at) {
		const isNewDateNewer =
			compareDates(category.updated_at, previous.updated_at) > 0
		return isNewDateNewer
	}
	return false
}

export const getCategories = () => {
	return Object.values(categoriesById || {})
}

// return categories that have groups that are active (not hidden), this filters out any categories that only has 1 group and that groups is hidden
export const getActiveCategories = () => {
	const categories = Object.values(chatsById || {})
		.filter((chat) => chat.category_id && !chat.isdm && !isChatHidden(chat.id))
		.map((c) => getCategory(c.category_id))
		.filter((c) => c)
	return Array.from(new Set(categories)).sort(sortCategoriesByTitle)
}

export const getCategoryForChat = (chatId) => {
	if (!chatId) return null
	const categoryId = getChat(chatId).category_id
	return categoriesById[categoryId]
}

// Stats related functions

export const normalizeStat = (stat) => {
	if (!stat || (!stat.chat_id && !stat.bubble_id)) return null
	stat.chat_id = stat.chat_id || stat.bubble_id
	stat.bubble_id = stat.chat_id // TODO: remove this once the migration is complete
	stat.unread_count = stat.unread_count || stat.unread
	stat.unread = stat.unread_count // TODO: remove this once the migration is complete
	stat.unread_mentions_count =
		stat.unread_mentions_count || stat.unread_mentions
	stat.unread_mentions = stat.unread_mentions_count // TODO: remove this once the migration is complete
	stat.updated_at = stat.updated_at || stat.updated
	stat.updated = stat.updated_at // TODO: remove this once the migration is complete
	return stat
}

export const setStat = (stat, skipDB) => {
	stat = normalizeStat(stat)
	if (!stat || isOldStats(stat)) return null
	statsByChatId[stat.chat_id] = stat

	const chat = getChat(stat.chat_id)
	if (chat?.id) {
		const {
			unread,
			unread_mentions,
			mark,
			latest_message_id,
			latest_message_created_at,
			updated_at,
		} = stat
		updateChat(stat.chat_id, {
			unread,
			unread_mentions,
			mark,
			latest_message_id,
			latest_message_created_at,
			stat_updated_at: updated_at,
		})
	}

	if (!skipDB) {
		store.dbPut('stats', stat)
	}
	EventBus.$emitDebounced('chat-update')
	EventBus.$emitDebounced('stat-update', stat)
	return stat
}

export const setStats = (stats, skipDB) => {
	if (!stats) return null
	const normalized = stats
		.map((stat) => {
			const normal = setStat(stat, true)
			if (normal?.bubble_id === store.currentChatId) {
				EventBus.$emitDebounced('stat-update', normal)
			}
			return normal
		})
		.filter((s) => s)
	if (normalized.length && !skipDB) {
		store.dbBulkPut('stats', normalized)
	}
	EventBus.$emitDebounced('chat-update')
	return normalized
}

export const updateStat = (id, s = {}) => {
	const stat = getStat(id) || {}
	setStat({ ...stat, ...s }, false)
}

export const getStat = (chatId) => {
	if (!chatId) return null
	return statsByChatId[chatId] || {}
}

const isOldStats = (stats) => {
	if (!stats) return null
	const previous = getStat(stats.bubble_id)
	if (previous && stats.updated && previous.updated) {
		const isNewDateNewer = compareDates(stats.updated, previous.updated) > 0
		return isNewDateNewer
	}
	return false
}

// Videosession Related Functions

// TODO: rename 'videosession' to 'meeting' in each of the following functions
export const normalizeVideosession = (videosession, skipDB) => {
	if (!videosession) return null
	const prevSession = getVideosession(videosession.id)
	const { host, endedby, ...rest } = videosession
	rest.chat_id = rest.chat_id || rest.bubble_id
	rest.bubble_id = rest.chat_id // TODO: remove this once the migration is complete
	if (host) {
		rest.host_id = host.id
		setUser(host, skipDB)
	} else if (prevSession?.host_id) {
		rest.host_id = prevSession?.host_id
	}
	if (endedby) {
		rest.endedby_id = host.id
		setUser(endedby, skipDB)
	} else if (prevSession?.endedby_id) {
		rest.endedby_id = prevSession?.endedby_id
	}
	return rest
}

export const setVideosession = (videosession, skipDB) => {
	if (!videosession) return null
	if (isOldVideosession(videosession)) {
		videosession = getVideosession(videosession.id)
	} else {
		videosession = normalizeVideosession(videosession, skipDB)
		videoSessionsById[videosession.id] = videosession
	}

	EventBus.$emitDebounced('chat-update')
	EventBus.$emitDebounced('videosession-update', videosession.id)

	return videosession
}

export const getVideosession = (videosessionId) => {
	if (!videosessionId) return null
	return videoSessionsById[videosessionId]
}

export const deleteVideosession = (id) => {
	const videosession = getVideosession(id)
	if (!videosession) return null
	delete videoSessionsById[id]

	EventBus.$emitDebounced('chat-update')
}

const isOldVideosession = (videosession) => {
	if (!videosession) return null
	const previous = getVideosession(videosession.id)
	if (previous && videosession.updated_at && previous.updated_at) {
		const isNewDateNewer =
			compareDates(videosession.updated_at, previous.updated_at) > 0
		return isNewDateNewer
	}
	return false
}

export function sortChatsByActivity(a, b) {
	// Sorting chats by activity or recency is broken into 3 levels or groupings
	// See: https://www.notion.so/hitlabs/12a2c86f24a6802fbf3ffe9537d6bbdf?pvs=4#083b7d8b04f342949bf0e80e846421c4
	// Sorting "Level 1"
	const aLevel1 = a.unread_mentions || a.marked_unread
	const bLevel1 = b.unread_mentions || b.marked_unread
	if (aLevel1 && bLevel1) return sortChatsByMarkedUnread(a, b) // both qualify for level 1
	if (aLevel1) return -1 // only a qualifies, put a first
	if (bLevel1) return 1 // only b qualifies, put b first
	// Sorting "Level 2"
	if (a.unread && b.unread) return sortChatsByMarkedUnread(a, b) // both qualify for level 2
	if (a.unread) return -1 // only a qualifies, put a first
	if (b.unread) return 1 // only b qualifies, put b first
	// Sorting "Level 3"
	return sortChatsByMarkedUnread(a, b)
}
